import React, { useEffect, useRef, useState } from 'react';
import { QUEST_MAPS } from '@app/lib/questMap';
import type { QuestId } from '@app/lib/site';
import styles from './style.module.css';

interface Props {
  quest: QuestId;
  alt: string;
  /** Small caption shown over the map until a photo is opened. */
  hint?: string;
}

const SWIPE_PX = 40;

export const InteractiveMap: React.FC<Props> = ({ quest, alt, hint }) => {
  const map = QUEST_MAPS[quest];
  const [active, setActive] = useState<number | null>(null);
  // Hover only previews a photo; a click/tap pins it open so it can be flipped
  // through. On touch the emulated mouseenter fires before the click, so the
  // first tap lands straight in the pinned state.
  const [pinned, setPinned] = useState(false);
  const touchX = useRef<number | null>(null);

  const order = map ? map.hotspots.map((h) => h.n).sort((a, b) => a - b) : [];

  const close = () => {
    setActive(null);
    setPinned(false);
  };

  const step = (d: number) => {
    setActive((a) => {
      if (a === null || order.length === 0) return a;
      const k = order.indexOf(a);
      return order[(k + d + order.length) % order.length];
    });
  };

  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned]);

  // Warm up the neighbours so flipping feels instant.
  useEffect(() => {
    if (!map || !pinned || active === null) return;
    const k = order.indexOf(active);
    [1, -1].forEach((d) => {
      const n = order[(k + d + order.length) % order.length];
      new Image().src = `${map.photoBase}/${n}.jpg`;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pinned]);

  if (!map) return null;

  const current = map.hotspots.find((h) => h.n === active) || null;

  const flip = (d: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    step(d);
  };

  // The dim and the opened photo sit outside .wrap, so they are sized by the
  // surrounding slide rather than the (often narrower) map image.
  return (
    <>
      <div className={styles.wrap} style={{ aspectRatio: map.ratio }}>
        <img src={map.src} alt={alt} className={styles.map} loading="lazy" />

        {hint && !current && <span className={styles.hint}>{hint}</span>}

        {map.hotspots.map((hs) => (
          <button
            key={hs.n}
            type="button"
            className={`${styles.spot} ${active === hs.n ? styles.spotActive : ''}`}
            style={{ left: `${hs.x}%`, top: `${hs.y}%` }}
            aria-label={`${alt} — ${hs.n}`}
            onMouseEnter={() => !pinned && setActive(hs.n)}
            onMouseLeave={() => !pinned && setActive((a) => (a === hs.n ? null : a))}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActive(hs.n);
              setPinned(true);
            }}
          />
        ))}
      </div>

      {current && (
        <>
          {/* Dim only focuses the photo. While previewing on hover it is
              click-through so the cursor keeps tracking the polaroids; once
              pinned it closes the photo. */}
          <button
            type="button"
            className={`${styles.backdrop} ${pinned ? styles.pinned : ''}`}
            aria-label="Close"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close();
            }}
          />
          <span
            className={`${styles.popup} ${pinned ? styles.pinned : ''}`}
            data-map-photo-open={pinned || undefined}
            onTouchStart={(e) => {
              touchX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              touchX.current = null;
              if (Math.abs(dx) > SWIPE_PX) step(dx < 0 ? 1 : -1);
            }}
          >
            <img key={current.n} src={`${map.photoBase}/${current.n}.jpg`} alt={`${alt} ${current.n}`} draggable={false} />
            <span className={styles.badge}>{current.n}</span>
            {pinned && (
              <>
                <button
                  type="button"
                  className={styles.close}
                  aria-label="Close"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    close();
                  }}
                >
                  ×
                </button>
                <button type="button" className={`${styles.flip} ${styles.flipPrev}`} aria-label="Previous stop" onClick={flip(-1)}>
                  ‹
                </button>
                <button type="button" className={`${styles.flip} ${styles.flipNext}`} aria-label="Next stop" onClick={flip(1)}>
                  ›
                </button>
              </>
            )}
          </span>
        </>
      )}
    </>
  );
};

export default InteractiveMap;
